import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
	FileExportOutline,
	XOutline as CloseFilledIcon,
	FileTypePptOutline as PowerpointIcon,
	WorldWwwOutline as OnlineIcon
} from "@launchmetrics/lm-icons";

import { State } from "store/types";
import { Online } from "./Form/Online";
import { PowerPoint } from "./Form/PowerPoint";

import {
	Button,
	Modal,
	ModalActions,
	ModalContent,
	ModalDescription,
	ModalHeading,
	ModalTrigger,
	Box
} from "@launchmetrics/lm-ui";
import { resetReport, setCloseWithoutSavingModalOpen, setSaveReportModalOpen } from 'store/article/report/reducers';
import { SidePanel } from "components/common/SidePanel/SidePanel";
import { isCurrentPage } from 'lib/pathCheck';
import { SidePanelContent, SidePanelFooter, SidePanelHeader } from "components/common/SidePanel";


import './ReportConfiguration.scss';
import { SaveReportModal } from "./SaveReportModal/SaveReportModal";

const VALID_REPORT_TYPES = ['online', 'powerpoint']; 

export const ReportConfiguration: React.FC = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const [openWarningModal, reportId, reportType, reportName, location, formDisabled] = useSelector((state: State) => [
		state.article.report.closeWithoutSavingModalOpen,
		state.article.report.configuration.id,
		state.article.report.configuration.type,
		state.article.report.configuration.name,
		state.router.location,
		state.article.report.formDisabled
	]);

	const [reportIcon, reportForm] = useMemo(() => {
		switch (reportType) {
			case 'online': return [<OnlineIcon styleProps={{width: 20, height: 20}} />, <Online />];
			case 'powerpoint': return [<PowerpointIcon styleProps={{width: 20, height: 20}} />, <PowerPoint />];
			default: return ['', ''];
		}
	}, [reportType]);

	const showReportConfiguration = useMemo(() => {
		const validRoute = isCurrentPage(location.pathname, 'article');
		if (!reportType || !VALID_REPORT_TYPES.includes(reportType) || !validRoute) {
			dispatch(resetReport());	
			return false;
		}
		return true;
	}, [reportType, location.pathname, dispatch]);

	const handleModalSave = useCallback(() => {
		dispatch(setCloseWithoutSavingModalOpen(false));
		dispatch(setSaveReportModalOpen(true));
	},[dispatch]);


	const header = useMemo(() => (
		<>
			<Box>
				<Box className='side-panel-header-title'>
					{reportIcon} {reportId ? reportName : t(`reports.side_panel.header_title.${reportType}`)}
				</Box>
			</Box>
			<Box id={'warning-modal-container'}>
				<Modal variant="aligned" open={openWarningModal} onOpenChange={open => dispatch(setCloseWithoutSavingModalOpen(open))}>
					<ModalTrigger>
						<Button
							data-testid='closeBtn'
							id="closeBtn"
							onClick={() => dispatch(formDisabled ? resetReport() : { type: 'report/isSameReportDefinition' })}
							styleProps={{ padding: 0, minHeight: 0, marginRight: -8, marginTop: -8 }}
							variant='textGray' 
							size='medium'
							leftIcon={<CloseFilledIcon />}
						/>
					</ModalTrigger>
					<ModalContent displayClose={false} styleProps={{ zIndex: 30 }} onPressEnter={handleModalSave}>
						<ModalHeading variant={"heading2XsSemibold"} color={'text.default'}>
							{t('reports.side_panel.close_without_saving_modal.header')}
						</ModalHeading>
						<ModalDescription color={'text.low'}>
							{t('reports.side_panel.close_without_saving_modal.description')}
						</ModalDescription>
						<ModalActions styleProps={{ justifyContent: 'flex-end'}}>
							<Button
								name={t('reports.side_panel.close_without_saving_modal.close')}
								variant={"tertiary"}
								onClick={() => {
									dispatch(resetReport());
									dispatch(setCloseWithoutSavingModalOpen(false));
								}}>
								{t('reports.side_panel.close_without_saving_modal.close')}
							</Button>
							<Button
								name={t('reports.side_panel.close_without_saving_modal.save')}
								onClick={handleModalSave}>
								{t('reports.side_panel.close_without_saving_modal.save')}
							</Button>
						</ModalActions>
					</ModalContent>
				</Modal>
			</Box>
		</>
	), [reportIcon, reportId, reportName, t, reportType, openWarningModal, handleModalSave, dispatch, formDisabled]);

	const footer = useMemo(() => {
		const disabledIconStyle = formDisabled ? { color: 'icon.disabled' } : {};
		return (
			<>
				<SaveReportModal />
				<Button
					onClick={() => dispatch({ type: 'report/exportReport' })}
					variant='primary'
					size='medium'
					leftIcon={<FileExportOutline styleProps={disabledIconStyle} />}
					disabled={formDisabled}
				>
					{t('reports.side_panel.footer.export')}
				</Button>
			</>
		);
	}, [formDisabled, t, dispatch]);

	return (
		<>
			{showReportConfiguration &&
				<SidePanel>
					<SidePanelHeader>
						{header}
					</SidePanelHeader>

					<SidePanelContent>
						<Box className='side-panel-header-text'>{t(`reports.side_panel.header_text.${reportType}`)}</Box>
						{reportForm}
					</SidePanelContent>

					<SidePanelFooter>
						{footer}
					</SidePanelFooter>
				</SidePanel>
			}
		</>
	);
};
